import type { ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '@meno/ui/lib/utils'

// Shared empty-state used across the signage pages (Content Hub, Audio Hub,
// Assets…). Borderless AND bg-less — mirrors the Groups empty state: just a
// centered icon tile → "No <items> yet" → a short description → optional action,
// filling the panel via min-h so it reads as part of the page rather than a
// nested card.
//
// Height: `h-full` fills when the parent is a bounded flex column; the
// viewport-based `min-h` fills the visible content area even on pages that just
// flow (where `h-full` resolves to auto). The `100vh - 17rem` offset accounts
// for the TopBar, panel padding, and a page's tab/search rows.
export default function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  className,
  iconClassName,
}: {
  icon: LucideIcon
  title: string
  description?: string
  action?: ReactNode
  /** Extra classes on the wrapper (e.g. `col-span-full` inside a grid). */
  className?: string
  iconClassName?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center h-full min-h-[calc(100vh-17rem)] py-16 gap-3',
        className,
      )}
    >
      <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mb-1">
        <Icon className={cn('w-7 h-7 text-primary', iconClassName)} />
      </div>
      <h2 className="text-lg font-semibold text-foreground">{title}</h2>
      {description && <p className="text-sm text-muted-foreground max-w-md leading-relaxed">{description}</p>}
      {action}
    </div>
  )
}
